<template>
  <div class="page-container">
    <div class="header-row">
      <h2 class="page-title">{{ $t('household.warehouses.title') }}</h2>
      <button class="btn-primary" @click="openDialog()">
        <LucideIcon :size="16" class="mr-2">
          <path d="M5 12h14"/><path d="M12 5v14"/>
        </LucideIcon>
        {{ $t('household.warehouses.addBtn') }}
      </button>
    </div>

    <!-- Поиск -->
    <div class="filter-card">
      <div class="search-wrapper">
        <LucideIcon :size="18" class="search-icon">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </LucideIcon>
        <input
          v-model="searchTerm"
          type="text"
          :placeholder="$t('household.warehouses.searchPlaceholder')"
          class="search-input"
        />
      </div>
    </div>

    <!-- Карточки складов -->
    <div class="cards-grid">
      <div v-for="warehouse in filteredWarehouses" :key="warehouse.id" class="warehouse-card">
        <div class="card-content">
          <div class="card-top">
            <div class="card-icon-title">
              <div class="icon-box">
                <LucideIcon :size="24" strokeWidth="1.5" class="text-blue">
                  <path d="M22 21V9a2 2 0 0 0-2-2l-8-5-8 5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"/><path d="M7 21v-4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4"/>
                </LucideIcon>
              </div>
              <div class="info-group">
                <h3 class="name">{{ warehouse.name }}</h3>
                <p class="address">
                  <LucideIcon :size="12" class="mr-1">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                  </LucideIcon>
                  {{ warehouse.address }}
                </p>
              </div>
            </div>
            <div class="actions">
              <button class="action-btn edit" @click="openDialog(warehouse)">
                <LucideIcon :size="16">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </LucideIcon>
              </button>
              <button class="action-btn delete" @click="handleDelete(warehouse.id)">
                <LucideIcon :size="16">
                  <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                </LucideIcon>
              </button>
            </div>
          </div>

          <!-- Ответственный -->
          <div v-if="getResponsibleName(warehouse)" class="responsible-row">
            <LucideIcon :size="14" class="mr-1 text-gray">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </LucideIcon>
            <span class="responsible-text">{{ getResponsibleName(warehouse) }}</span>
          </div>

          <!-- Структура склада -->
          <div class="zones-section" v-if="warehouse.structure && warehouse.structure.length">
            <p class="section-label">{{ $t('household.warehouses.structure') }}</p>
            <div class="zones-summary">
              <div v-for="zone in warehouse.structure" :key="zone.id" class="zone-item">
                <div class="zone-header">
                  <LucideIcon :size="12" class="mr-1 text-blue"><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/></LucideIcon>
                  <span class="zone-name">{{ zone.name }}</span>
                </div>
                <div class="zone-details">
                  <span :title="$t('household.warehouses.rowsTitle')">{{ zone.rows }}{{ $t('household.warehouses.rowsShort') }}</span>
                  <span :title="$t('household.warehouses.racksTitle')">{{ zone.racks }}{{ $t('household.warehouses.racksShort') }}</span>
                  <span :title="$t('household.warehouses.levelsTitle')">{{ zone.levels }}{{ $t('household.warehouses.levelsShort') }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Статистика -->
          <div class="stats-row">
            <div class="stat-item">
              <p class="stat-label">{{ $t('household.warehouses.positions') }}</p>
              <p class="stat-value">0</p>
            </div>
            <div class="stat-item">
              <p class="stat-label">{{ $t('household.warehouses.totalItems') }}</p>
              <p class="stat-value">0</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="filteredWarehouses.length === 0" class="empty-state">
      {{ $t('household.warehouses.notFound') }}
    </div>

    <!-- Диалог -->
    <div v-if="isDialogOpen" class="modal-overlay" @click.self="closeDialog">
      <div class="modal-content large">
        <h3 class="modal-title">
          {{ editingItem ? $t('household.warehouses.dialogEditTitle') : $t('household.warehouses.dialogAddTitle') }}
        </h3>
        <form @submit.prevent="handleSubmit" class="modal-form-scroll">
          <div class="form-body">
            <div class="form-group">
              <label>{{ $t('household.warehouses.nameLabel') }}</label>
              <input v-model="formData.name" type="text" required :placeholder="$t('household.warehouses.namePlaceholder')" />
            </div>

            <div class="form-group">
              <label>{{ $t('household.warehouses.addressLabel') }}</label>
              <input v-model="formData.address" type="text" required :placeholder="$t('household.warehouses.addressPlaceholder')" />
            </div>

            <div class="form-group">
              <label>{{ $t('household.warehouses.responsibleLabel') }}</label>
              <div class="styled-select-wrapper">
                <select v-model="formData.user_id" class="styled-select" required>
                  <option value="">{{ $t('household.warehouses.responsiblePlaceholder') }}</option>
                  <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name || u.username || `${$t('household.shared.userPrefix')} ${u.id}` }}</option>
                </select>
                <LucideIcon :size="16" class="select-icon">
                  <path d="m6 9 6 6 6-6"/>
                </LucideIcon>
              </div>
            </div>

            <!-- Секция структуры (только если склад сохранен) -->
            <div v-if="editingItem || isSavingAfterInitial" class="structure-tree-section">
              <h3 class="section-title mb-3">{{ $t('household.warehouses.manageStructure') }}</h3>

              <div class="tree-container">
                <div class="tree-header mb-3">
                  <div v-if="!isAddingZone" class="flex-row">
                    <button type="button" class="btn-add-node" @click="isAddingZone = true">
                      <LucideIcon :size="14" class="mr-1"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                      {{ $t('household.warehouses.addZoneBtn') }}
                    </button>
                  </div>
                  <div v-else class="add-zone-inline">
                    <input
                      v-model="newZoneName"
                      type="text"
                      :placeholder="$t('household.warehouses.zonePlaceholder')"
                      class="inline-input"
                      @keyup.enter="handleAddNode('zone')"
                    />
                    <div class="inline-actions">
                      <button type="button" class="btn-inline-save" @click="handleAddNode('zone')">
                        <LucideIcon :size="14"><path d="M20 6L9 17l-5-5"/></LucideIcon>
                      </button>
                      <button type="button" class="btn-inline-cancel" @click="isAddingZone = false; newZoneName = ''">
                        <LucideIcon :size="14"><path d="m18 6-12 12"/><path d="m6 6 12 12"/></LucideIcon>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="tree-root">
                  <div v-for="zone in structureTree" :key="zone.id" class="tree-node">
                    <div class="node-content zone-node" @click="toggleNode(zone)">
                      <LucideIcon :size="16" class="node-icon" :class="{ rotated: zone.expanded }">
                        <path d="m9 18 6-6-6-6"/>
                      </LucideIcon>
                      <span class="node-label">{{ $t('household.warehouses.zoneLabelPrefix') }} {{ zone.name }}</span>
                      <div class="node-actions" @click.stop>
                        <button type="button" class="btn-node-action add" @click="handleAddNode('row', zone)">
                          <LucideIcon :size="12"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                        </button>
                        <button type="button" class="btn-node-action delete" @click="handleDeleteNode('zone', zone, structureTree)">
                          <LucideIcon :size="12"><path d="M5 12h14"/></LucideIcon>
                        </button>
                      </div>
                    </div>
                    
                    <div v-if="zone.expanded" class="node-children">
                      <div v-for="row in zone.children" :key="row.id" class="tree-node">
                        <div class="node-content row-node" @click="toggleNode(row)">
                          <LucideIcon :size="16" class="node-icon" :class="{ rotated: row.expanded }">
                            <path d="m9 18 6-6-6-6"/>
                          </LucideIcon>
                          <span class="node-label">{{ row.name }}</span>
                          <div class="node-actions" @click.stop>
                            <button type="button" class="btn-node-action add" @click="handleAddNode('rack', row)">
                              <LucideIcon :size="12"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                            </button>
                            <button type="button" class="btn-node-action delete" @click="handleDeleteNode('row', row, zone.children)">
                              <LucideIcon :size="12"><path d="M5 12h14"/></LucideIcon>
                            </button>
                          </div>
                        </div>

                        <div v-if="row.expanded" class="node-children">
                          <div v-for="rack in row.children" :key="rack.id" class="tree-node">
                            <div class="node-content rack-node" @click="toggleNode(rack)">
                              <LucideIcon :size="16" class="node-icon" :class="{ rotated: rack.expanded }">
                                <path d="m9 18 6-6-6-6"/>
                              </LucideIcon>
                              <span class="node-label">{{ rack.name }}</span>
                              <div class="node-actions" @click.stop>
                                <button type="button" class="btn-node-action add" @click="handleAddNode('level', rack)">
                                  <LucideIcon :size="12"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                                </button>
                                <button type="button" class="btn-node-action delete" @click="handleDeleteNode('rack', rack, row.children)">
                                  <LucideIcon :size="12"><path d="M5 12h14"/></LucideIcon>
                                </button>
                              </div>
                            </div>

                            <div v-if="rack.expanded" class="node-children">
                              <div v-for="level in rack.children" :key="level.id" class="tree-node">
                                <div class="node-content level-node">
                                  <span class="node-label">{{ level.name }}</span>
                                  <div class="node-actions" @click.stop>
                                    <button type="button" class="btn-node-action delete" @click="handleDeleteNode('level', level, rack.children)">
                                      <LucideIcon :size="12"><path d="M5 12h14"/></LucideIcon>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="initial-creation-info">
              <LucideIcon :size="24" class="mb-2 text-blue"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></LucideIcon>
              <p>{{ $t('household.warehouses.initialCreationInfo') }}</p>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-secondary large flex-1" @click="closeDialog" :disabled="isSaving">{{ $t('common.close') }}</button>
            <button type="submit" class="btn-primary-modal large flex-1" :disabled="isSaving">
              <span v-if="isSaving" class="loader mr-2"></span>
              {{ isSaving ? $t('household.warehouses.savingBtn') : (editingItem ? $t('household.warehouses.saveChangesBtn') : $t('household.warehouses.createBtn')) }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { 
  getWarehouses, 
  createWarehouse, 
  updateWarehouse, 
  deleteWarehouse,
  getWarehouseZones,
  createWarehouseZone,
  deleteWarehouseZone,
  getRows,
  createRow,
  deleteRow,
  getRacks,
  createRack,
  deleteRack,
  getLevels,
  createLevel,
  deleteLevel
} from '@/api/household_warehouses';
import { getUsers } from '@/api/user';

const { t } = useI18n();
const warehouses = ref([]);
const searchTerm = ref('');
const isDialogOpen = ref(false);
const isSaving = ref(false);
const isSavingAfterInitial = ref(false);
const editingItem = ref(null);
const currentCreatedId = ref(null);
const structureTree = ref([]);
const isAddingZone = ref(false);
const newZoneName = ref('');
const users = ref([]);

const formData = ref({
  name: '',
  address: '',
  responsible: '',
  user_id: '',
});

const filteredWarehouses = computed(() => {
  return warehouses.value.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
    item.address.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

const sortStructure = (items) => {
  return [...items].sort((a, b) => {
    // Try to extract numbers for natural sorting (e.g., "Ряд 2" before "Ряд 10")
    const aNum = parseInt(a.name.match(/\d+/) || 0);
    const bNum = parseInt(b.name.match(/\d+/) || 0);
    if (aNum && bNum) return aNum - bNum;
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const loadWarehouses = async () => {
  try {
    const res = await getWarehouses();
    const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    
    // Fetch structure summary for each warehouse (for dashboard)
    const enrichedData = await Promise.all(data.map(async (wh) => {
      try {
        const zonesRes = await getWarehouseZones({ warehouse: wh.id });
        const zones = sortStructure(Array.isArray(zonesRes.data) ? zonesRes.data : (zonesRes.data?.results || []));
        
        const structure = await Promise.all(zones.map(async (zone) => {
          const [rowsRes, racksRes, levelsRes] = await Promise.all([
            getRows({ zone: zone.id }),
            getRacks({ row__zone: zone.id }),
            getLevels({ rack__row__zone: zone.id })
          ]);
          return {
            id: zone.id,
            name: zone.name,
            rows: (Array.isArray(rowsRes.data) ? rowsRes.data : (rowsRes.data?.results || [])).length,
            racks: (Array.isArray(racksRes.data) ? racksRes.data : (racksRes.data?.results || [])).length,
            levels: (Array.isArray(levelsRes.data) ? levelsRes.data : (levelsRes.data?.results || [])).length
          };
        }));
        return { ...wh, structure };
      } catch (e) {
        return { ...wh, structure: [] };
      }
    }));
    warehouses.value = enrichedData;
  } catch (e) {
    console.error('Failed to load warehouses:', e);
  }
};

const loadUsers = async () => {
  try {
    const list = await getUsers();
    users.value = Array.isArray(list) ? list : (list?.results || list?.data || []);
  } catch (e) {
    console.error('Failed to load users:', e);
  }
};

const getResponsibleName = (warehouse) => {
  // First check if responsible text is already set
  if (warehouse.responsible) {
    return warehouse.responsible;
  }
  
  // Then try to get from user
  const userId = warehouse.user_id || warehouse.user?.id;
  if (userId) {
    const user = users.value.find(u => u.id === userId);
    if (user) return user.full_name || user.username || `${t('household.shared.userPrefix')} ${userId}`;
    return `${t('household.shared.userPrefix')} ${userId}`;
  }

  return '';
};

const loadStructureSubTree = async (item, type) => {
  try {
    let res;
    if (type === 'zone') res = await getRows({ zone: item.id });
    else if (type === 'row') res = await getRacks({ row: item.id });
    else if (type === 'rack') res = await getLevels({ rack: item.id });
    
    const children = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    item.children = sortStructure(children).map(child => ({
      ...child,
      type: type === 'zone' ? 'row' : (type === 'row' ? 'rack' : 'level'),
      expanded: false,
      children: []
    }));
  } catch (e) {
    console.error(`Failed to load children for ${type} ${item.id}`, e);
  }
};

const toggleNode = async (node) => {
  node.expanded = !node.expanded;
  if (node.expanded && node.children.length === 0) {
    await loadStructureSubTree(node, node.type);
  }
};

const handleAddNode = async (type, parent = null) => {
  let name = '';
  if (type === 'zone') {
    if (!newZoneName.value.trim()) {
      newZoneName.value = `${t('household.warehouses.defaultZoneName')} ${structureTree.value.length + 1}`;
    }
    name = newZoneName.value.trim();
  } else {
    const prefix = type === 'row' ? t('household.warehouses.rowPrefix') : (type === 'rack' ? t('household.warehouses.rackPrefix') : t('household.warehouses.levelPrefix'));
    name = `${prefix} ${parent.children.length + 1}`;
  }

  try {
    let res;
    const warehouseId = editingItem.value?.id || currentCreatedId.value;
    
    if (type === 'zone') {
      res = await createWarehouseZone({ warehouse: warehouseId, name });
      structureTree.value.push({ ...res.data, type: 'zone', expanded: false, children: [] });
      structureTree.value = sortStructure(structureTree.value);
      isAddingZone.value = false;
      newZoneName.value = '';
    } else if (type === 'row') {
      res = await createRow({ zone: parent.id, name });
      parent.children.push({ ...res.data, type: 'row', expanded: false, children: [] });
      parent.children = sortStructure(parent.children);
      parent.expanded = true;
    } else if (type === 'rack') {
      res = await createRack({ row: parent.id, name });
      parent.children.push({ ...res.data, type: 'rack', expanded: false, children: [] });
      parent.children = sortStructure(parent.children);
      parent.expanded = true;
    } else if (type === 'level') {
      res = await createLevel({ rack: parent.id, name });
      parent.children.push({ ...res.data, type: 'level', expanded: false, children: [] });
      parent.children = sortStructure(parent.children);
      parent.expanded = true;
    }
    // Update dashboard summary
    loadWarehouses();
  } catch (e) {
    alert(t('household.warehouses.createError'));
  }
};

const handleDeleteNode = async (type, node, collection) => {
  try {
    if (type === 'zone') await deleteWarehouseZone(node.id);
    else if (type === 'row') await deleteRow(node.id);
    else if (type === 'rack') await deleteRack(node.id);
    else if (type === 'level') await deleteLevel(node.id);

    const idx = collection.findIndex(item => item.id === node.id);
    if (idx !== -1) collection.splice(idx, 1);

    loadWarehouses();
  } catch (e) {
    alert(t('household.warehouses.deleteError'));
  }
};

const openDialog = async (item = null) => {
  editingItem.value = item;
  isSavingAfterInitial.value = false;
  currentCreatedId.value = null;
  isAddingZone.value = false;
  newZoneName.value = '';
  
  if (item) {
    formData.value.name = item.name;
    formData.value.address = item.address;
    formData.value.responsible = item.responsible || '';
    formData.value.user_id = item.user?.id || item.user_id || '';
    const res = await getWarehouseZones({ warehouse: item.id });
    const zones = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    structureTree.value = sortStructure(zones).map(z => ({
      ...z,
      type: 'zone',
      expanded: false,
      children: []
    }));
  } else {
    formData.value.name = '';
    formData.value.address = '';
    formData.value.responsible = '';
    formData.value.user_id = '';
    structureTree.value = [];
  }
  isDialogOpen.value = true;
};

const closeDialog = () => {
  isDialogOpen.value = false;
};

const handleSubmit = async () => {
  isSaving.value = true;
  
  // Get responsible name from selected user
  const selectedUser = users.value.find(u => u.id === Number(formData.value.user_id));
  const responsibleName = selectedUser?.full_name || selectedUser?.username || '';
  
  const data = { 
    name: formData.value.name, 
    address: formData.value.address,
    responsible: responsibleName,
    user_id: Number(formData.value.user_id)
  };
  
  try {
    if (editingItem.value) {
      await updateWarehouse(editingItem.value.id, data);
      closeDialog();
    } else {
      const res = await createWarehouse(data);
      currentCreatedId.value = res.data.id;
      isSavingAfterInitial.value = true;
    }
    await loadWarehouses();
  } catch (error) {
    console.error('Failed to save warehouse:', error);
    alert(t('household.warehouses.saveError'));
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async (id) => {
  if (confirm(t('household.warehouses.confirmDelete'))) {
    await deleteWarehouse(id);
    await loadWarehouses();
  }
};

onMounted(() => {
  loadWarehouses();
  loadUsers();
});
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }

.filter-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.search-wrapper { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search-input {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 2rem;
  padding: 0.75rem 1rem 0.75rem 3rem;
  outline: none;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}

.warehouse-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; }
.card-icon-title { display: flex; gap: 1rem; align-items: center; }

.icon-box {
  width: 3.5rem;
  height: 3.5rem;
  background: #eff6ff;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.text-blue { color: #3b82f6; }

.name { color: #030229; font-weight: 700; margin: 0; font-size: 1.25rem; }
.address { color: #717182; font-size: 0.85rem; margin-top: 0.25rem; display: flex; align-items: center; }

.responsible-row { display: flex; align-items: center; margin-top: 0.75rem; padding: 0.5rem 0; border-top: 1px solid #f1f5f9; }
.responsible-text { color: #4b5563; font-size: 0.875rem; font-weight: 500; }
.text-gray { color: #9ca3af; }

.actions { display: flex; gap: 0.5rem; }
.action-btn {
  background: #f8fafc;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  color: #3b82f6;
}
.action-btn.delete { color: #ef4444; }

.zones-section { margin-bottom: 1.5rem; }
.section-label { font-size: 0.75rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem; }

.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #f1f1f4;
}
.stat-item { text-align: center; }
.stat-label { font-size: 0.7rem; color: #717182; margin-bottom: 0.25rem; }
.stat-value { font-size: 1rem; font-weight: 700; color: #030229; }

.empty-state { text-align: center; padding: 4rem; color: #717182; background: white; border-radius: 1rem; border: 1px dashed #e2e8f0; }

.btn-primary {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal-content.large {
  background: #ffffff;
  border-radius: 1.5rem;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.modal-title { color: #030229; font-size: 1.5rem; font-weight: 700; padding: 2rem 2rem 1rem; margin: 0; }

.modal-form-scroll { overflow-y: auto; flex: 1; }
.form-body { padding: 0 2rem 2rem; }

.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; color: #4b5563; font-size: 0.875rem; font-weight: 600; margin-bottom: 0.5rem; }
.form-group input {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  outline: none;
}

.initial-creation-info { 
  background: #f0f9ff; 
  padding: 1.5rem; 
  border-radius: 1rem; 
  border: 1px dashed #7dd3fc;
  color: #0369a1;
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.structure-tree-section { border-top: 1px solid #f1f5f9; padding-top: 2rem; }
.tree-container { background: #fbfbfc; border-radius: 1rem; padding: 1.5rem; border: 1px solid #e2e8f0; }

.tree-header { display: flex; align-items: center; }
.add-zone-inline { display: flex; gap: 0.5rem; width: 100%; }
.inline-input {
  flex: 1;
  background: white !important;
  border: 1px solid #3b82f6 !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.85rem !important;
}
.inline-actions { display: flex; gap: 0.25rem; }
.btn-inline-save, .btn-inline-cancel {
  border: none;
  border-radius: 0.4rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.btn-inline-save { background: #10b981; color: white; }
.btn-inline-cancel { background: #f1f5f9; color: #64748b; }

.btn-add-node {
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.tree-node { margin-bottom: 0.25rem; }
.node-content {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}
.node-content:hover { background: #f1f5f9; }

.zone-node { background: #eff6ff; border: 1px solid #dbeafe; }
.row-node { background: #fdf2f8; border: 1px solid #fce7f3; }
.rack-node { background: #f0fdf4; border: 1px solid #dcfce7; }
.level-node { background: #ffffff; border: 1px solid #f1f5f9; cursor: default; }

.node-icon { color: #94a3b8; transition: transform 0.2s; margin-right: 0.5rem; }
.node-icon.rotated { transform: rotate(90deg); }

.node-label { font-size: 0.85rem; font-weight: 600; color: #1e293b; flex: 1; }

.node-actions { display: flex; gap: 0.4rem; }
.btn-node-action {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-node-action.add { color: #64748b; }
.btn-node-action.add:hover { background: #2563eb; color: white; border-color: #2563eb; }
.btn-node-action.delete { color: #ef4444; }
.btn-node-action.delete:hover { background: #ef4444; color: white; border-color: #ef4444; }

.node-children { padding-left: 1.5rem; border-left: 1px dashed #e2e8f0; margin-left: 0.75rem; margin-top: 0.25rem; }

.modal-footer { padding: 1.5rem 2rem; background: #f8fafc; border-top: 1px solid #f1f5f9; display: flex; gap: 1rem; border-radius: 0 0 1.5rem 1.5rem; }

.flex-1 { flex: 1; }
.large { padding: 1rem !important; }

.zones-summary { display: flex; flex-direction: column; gap: 0.75rem; }
.zone-item { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  background: #f8fafc; 
  padding: 0.5rem 0.75rem; 
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
}
.zone-header { display: flex; align-items: center; }
.zone-name { font-size: 0.85rem; font-weight: 700; color: #030229; }
.zone-details { display: flex; gap: 0.75rem; }
.zone-details span { 
  font-size: 0.7rem; 
  font-weight: 600; 
  color: #64748b; 
  background: #ffffff; 
  padding: 0.15rem 0.4rem; 
  border-radius: 0.25rem;
  border: 1px solid #e2e8f0;
}

.loader {
  width: 14px;
  height: 14px;
  border: 2px solid #FFF;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 0.75rem; }

.styled-select-wrapper {
  position: relative;
}
.styled-select {
  width: 100%;
  appearance: none;
  background: linear-gradient(to bottom, #ffffff, #f9fafb);
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}
.styled-select:hover { border-color: #d1d5db; background: #ffffff; }
.styled-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); background: #ffffff; }
.select-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}
</style>
